// ── NavIcons — the "Solid Facet" glyph set for the main nav + More hub ────────
// Replaces lucide's stroke icons across the primary bar and the 21-item More
// sheet with filled silhouettes, following the same color-mix(var(--ic), …)
// technique already proven on FilmReel/Clapboard/ReelRating: every facet
// derives off the single `color` prop instead of a hardcoded gradient, so it
// stays correct whether `color` is currentColor, a hex, or a themed CSS var.
//
// Why filled instead of stroked: the Moon revert (2026-08-06) taught the real
// lesson — thin strokes and masked cutouts are what die first at the nav's
// actual 20px render size. A solid silhouette has no stroke width to lose and
// no crescent-style cutout to blur into noise, so it's the safer bet at small
// size, not just the bigger visual step up.
//
// Drop-in signature matches lucide + FilmReel: size, color="currentColor",
// strokeWidth (accepted for API compat, unused — these are filled, not
// stroked), plus className/style forwarded so the existing hub-tile idle
// animations and drop-shadow filter keep working unchanged.
const BASE = (color) => ({ '--ic': color })
const hi = (pct = 55, to = 'white') => `color-mix(in srgb, var(--ic) ${100 - pct}%, ${to} ${pct}%)`
const lo = (pct = 20) => `color-mix(in srgb, var(--ic) ${100 - pct}%, black ${pct}%)`

function Svg({ size, color, className, style, children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      className={className} style={{ ...BASE(color), ...style }}>
      {children}
    </svg>
  )
}

// ── Primary bar (4) ────────────────────────────────────────────────────────

export function HomeIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M12 3.2 L21 10.4 V20 a1 1 0 0 1 -1 1 H4 a1 1 0 0 1 -1 -1 V10.4 Z" fill="var(--ic)"/>
      <path d="M12 3.2 L21 10.4 V20 a1 1 0 0 1 -1 1 H12 Z" fill={hi()}/>
      <rect x="9.5" y="13.8" width="5" height="7.2" rx="0.6" fill={lo(70)} opacity="0.35"/>
    </Svg>
  )
}

export function MyListIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <rect x="3" y="5" width="18" height="4" rx="1.6" fill="var(--ic)"/>
      <rect x="3" y="10.5" width="13" height="4" rx="1.6" fill={hi(25)}/>
      <rect x="3" y="16" width="9" height="4" rx="1.6" fill="var(--ic)"/>
    </Svg>
  )
}

export function ProfileIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <circle cx="12" cy="7.5" r="4" fill="var(--ic)"/>
      <path d="M4 20.5 a8 7.5 0 0 1 16 0 a1 1 0 0 1 -1 1.2 H5 a1 1 0 0 1 -1 -1.2 Z" fill="var(--ic)"/>
      <path d="M12 3.5 a4 4 0 0 1 0 8 a5 5 0 0 0 0 -8 Z" fill={lo(40)}/>
    </Svg>
  )
}

// ── More hub (17) ───────────────────────────────────────────────────────────

export function DiscoverIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <circle cx="10.5" cy="10.5" r="7" fill="var(--ic)"/>
      <path d="M10.5 3.5 a7 7 0 0 1 0 14 a8.6 8.6 0 0 0 0 -14 Z" fill={hi(35)}/>
      <circle cx="10.5" cy="10.5" r="3.6" fill="var(--nav-bg, #07070f)" opacity="0.9"/>
      <rect x="18.5" y="17.2" width="4.6" height="3.2" rx="1.4" transform="rotate(45 18.5 17.2)" fill="var(--ic)"/>
    </Svg>
  )
}

export function ForYouIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M11.5 2.5 c0.6 3.3 1.8 4.7 5.3 5.5 c-3.5 0.8 -4.7 2.2 -5.3 5.5 c-0.6 -3.3 -1.8 -4.7 -5.3 -5.5 c3.5 -0.8 4.7 -2.2 5.3 -5.5 Z" fill="var(--ic)"/>
      <path d="M11.5 2.5 c0.6 3.3 1.8 4.7 5.3 5.5 c-2 0.45 -3.2 1.05 -4 2 c-0.35 -3.1 -1.35 -5 -1.3 -7.5 Z" fill={hi(35)}/>
      <path d="M18 14.5 c0.35 1.9 1 2.7 3 3.15 c-2 0.45 -2.65 1.25 -3 3.15 c-0.35 -1.9 -1 -2.7 -3 -3.15 c2 -0.45 2.65 -1.25 3 -3.15 Z" fill="var(--ic)"/>
    </Svg>
  )
}

export function CliffhangerIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M6 4 h8 a2 2 0 0 1 2 2 v2.3 a2.1 2.1 0 0 0 0 4.2 V16 a2 2 0 0 1 -2 2 H6 a2 2 0 0 1 -2 -2 v-1.8 a2.1 2.1 0 0 0 0 -4.2 V6 a2 2 0 0 1 2 -2 Z" fill="var(--ic)"/>
      <path d="M6 4 h8 a2 2 0 0 1 2 2 v2.3 a2.1 2.1 0 0 0 0 4.2 V16 a2 2 0 0 1 -0.9 1.66 C11.3 13.5 8.4 9.2 6.6 4.2 A2 2 0 0 1 6 4 Z" fill={hi(30)}/>
      <circle cx="10" cy="18.6" r="1.5" fill="var(--ic)"/>
    </Svg>
  )
}

export function AftershowIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M4 5 h16 a1.5 1.5 0 0 1 1.5 1.5 v9 a1.5 1.5 0 0 1 -1.5 1.5 H10 l-4.2 3.2 a0.6 0.6 0 0 1 -0.96 -0.48 V17 H4 a1.5 1.5 0 0 1 -1.5 -1.5 v-9 A1.5 1.5 0 0 1 4 5 Z" fill="var(--ic)"/>
      <path d="M4 5 h16 a1.5 1.5 0 0 1 1.5 1.5 v9 a1.5 1.5 0 0 1 -0.66 1.24 C15.5 12.6 8.6 8.7 4.2 5.06 A1.5 1.5 0 0 1 4 5 Z" fill={hi(30)}/>
    </Svg>
  )
}

export function CanonIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M5 3.5 h11 a1.5 1.5 0 0 1 1.5 1.5 v15.5 l-7 -3 l-7 3 V5 a1.5 1.5 0 0 1 1.5 -1.5 Z" fill="var(--ic)"/>
      <path d="M5 3.5 h11 a1.5 1.5 0 0 1 1.5 1.5 v15.5 l-3 -1.3 V5 a1.5 1.5 0 0 0 -1.5 -1.5 H5.6 A1.5 1.5 0 0 1 5 3.5 Z" fill={hi(30)}/>
      <rect x="3.2" y="3.5" width="2.2" height="16.5" rx="0.6" fill={lo(35)}/>
    </Svg>
  )
}

export function ShelvesIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <rect x="3" y="3" width="4.4" height="16" rx="1" fill="var(--ic)"/>
      <rect x="9" y="6" width="4.4" height="13" rx="1" fill={hi(30)}/>
      <rect x="15" y="4.5" width="4.4" height="14.5" rx="1" fill="var(--ic)"/>
      <rect x="2.5" y="20" width="17.4" height="1.6" rx="0.8" fill={lo(20)}/>
    </Svg>
  )
}

export function BuzzIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M13.2 2.5 L5.4 13.5 h5.1 l-1.2 8 l8.3 -11.3 h-5.3 Z" fill="var(--ic)"/>
      <path d="M13.2 2.5 L5.4 13.5 h5.1 l1.9 -2.6 h-3.4 L13.2 6 Z" fill={hi(35)}/>
    </Svg>
  )
}

export function ReviewsIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M19.5 3 c0.6 6 -1.4 11.4 -6.2 14.6 c-2.1 1.4 -4.5 2.1 -6.8 2.4 c1 -2 2.3 -3.6 3.9 -4.9 C7 15.7 4.3 15.9 2.5 17.3 c1.4 -4.4 5 -7.7 9.4 -9 C15.1 7.3 17.6 5.5 19.5 3 Z" fill="var(--ic)"/>
      <path d="M19.5 3 c0.6 6 -1.4 11.4 -6.2 14.6 c-0.9 0.6 -1.8 1.1 -2.8 1.5 c2 -3.8 3 -8.2 2.4 -13.3 C14.6 4.7 17 3.9 19.5 3 Z" fill={hi(30)}/>
    </Svg>
  )
}

export function ChallengesIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <rect x="4" y="3" width="1.8" height="18" rx="0.7" fill={lo(20)}/>
      <path d="M5.8 4 h13 a0.8 0.8 0 0 1 0.65 1.27 l-2.3 3.13 l2.3 3.13 A0.8 0.8 0 0 1 18.8 12.8 h-13 Z" fill="var(--ic)"/>
      <path d="M5.8 4 h13 a0.8 0.8 0 0 1 0.65 1.27 l-1.15 1.57 c-3 -0.1 -7.9 -1.03 -12.5 -2.6 A0.8 0.8 0 0 1 5.8 4 Z" fill={hi(30)}/>
    </Svg>
  )
}

export function NotificationsIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M12 2.5 a1.3 1.3 0 0 1 1.3 1.3 v0.6 c3 0.65 5.2 3.3 5.2 6.5 v3 l1.5 2.5 a0.8 0.8 0 0 1 -0.7 1.2 H4.7 a0.8 0.8 0 0 1 -0.7 -1.2 l1.5 -2.5 v-3 c0 -3.2 2.2 -5.85 5.2 -6.5 v-0.6 A1.3 1.3 0 0 1 12 2.5 Z" fill="var(--ic)"/>
      <path d="M12 2.5 a1.3 1.3 0 0 1 1.3 1.3 v0.6 c3 0.65 5.2 3.3 5.2 6.5 v3 l1.5 2.5 a0.8 0.8 0 0 1 -0.34 1.14 C16.8 13.4 13.6 8 12 2.52 Z" fill={hi(30)}/>
      <path d="M9 19.5 h6 a3 3 0 0 1 -6 0 Z" fill="var(--ic)"/>
    </Svg>
  )
}

export function PeopleIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <circle cx="9" cy="8" r="3.4" fill="var(--ic)"/>
      <path d="M3 20 a6 5.5 0 0 1 12 0 a1 1 0 0 1 -1 1.1 H4 a1 1 0 0 1 -1 -1.1 Z" fill="var(--ic)"/>
      <circle cx="16" cy="9" r="2.8" fill={hi(25)}/>
      <path d="M13.6 21.1 H21 a1 1 0 0 0 1 -1.1 c0 -2.7 -1.9 -4.9 -4.4 -5.4 a5.9 5.9 0 0 0 -3 0.55 A6 5.5 0 0 1 15.6 20 c0 0.4 -0.08 0.77 -0.22 1.1 Z" fill={hi(25)}/>
    </Svg>
  )
}

export function StatsIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  // The hub's idle "equalizer bounce" (.hubi.a-bars in HubIcons.css) targets
  // <line> children specifically (matching lucide's BarChart2 markup) with a
  // per-bar staggered scaleY — so these stay <line>s with a thick round cap
  // instead of <rect>s, or the animation would silently stop firing.
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <line x1="5.2" y1="21" x2="5.2" y2="13" stroke="var(--ic)" strokeWidth="4.4" strokeLinecap="round"/>
      <line x1="12" y1="21" x2="12" y2="7" stroke={hi(30)} strokeWidth="4.4" strokeLinecap="round"/>
      <line x1="18.8" y1="21" x2="18.8" y2="3" stroke="var(--ic)" strokeWidth="4.4" strokeLinecap="round"/>
    </Svg>
  )
}

export function HistoryIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <circle cx="12" cy="12" r="9" fill="var(--ic)"/>
      <path d="M12 3 a9 9 0 0 1 0 18 a11.2 11.2 0 0 0 0 -18 Z" fill={lo(40)}/>
      <path d="M12 6.5 a0.9 0.9 0 0 1 0.9 0.9 v4.2 l3.4 2 a0.9 0.9 0 0 1 -0.9 1.56 l-3.85 -2.27 a0.9 0.9 0 0 1 -0.45 -0.78 V7.4 A0.9 0.9 0 0 1 12 6.5 Z" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
    </Svg>
  )
}

export function CardsIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <rect x="2.5" y="5" width="19" height="14" rx="2.2" fill="var(--ic)"/>
      <path d="M4.7 5 h16.8 a2.2 2.2 0 0 1 2 2.2 V9 H2.5 V7.2 A2.2 2.2 0 0 1 4.7 5 Z" fill={lo(30)}/>
      <path d="M2.5 5 h19 a2.2 2.2 0 0 1 2 1.3 C16.3 9.4 8.6 9.3 2.6 7.1 A2.2 2.2 0 0 1 2.5 5 Z" fill={hi(30)}/>
      <rect x="4.5" y="14" width="6" height="2" rx="1" fill={lo(20)} opacity="0.5"/>
    </Svg>
  )
}

export function ImportIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M6.5 16 a4 4 0 0 1 0.4 -7.98 A5.5 5.5 0 0 1 17.4 9.2 A4 4 0 0 1 17 17 H6.5 a4 4 0 0 1 0 -1 Z" fill="var(--ic)"/>
      <path d="M6.5 16 a4 4 0 0 1 0.4 -7.98 a5.5 5.5 0 0 1 1.9 -2.86 c-0.55 3.9 -0.75 7.8 -0.35 11.84 A4 4 0 0 1 6.5 16 Z" fill={hi(30)}/>
      <path d="M12 10.5 a0.9 0.9 0 0 1 0.9 0.9 v4.1 l1.1 -1.1 a0.9 0.9 0 1 1 1.27 1.27 l-2.63 2.63 a0.9 0.9 0 0 1 -1.27 0 l-2.64 -2.63 a0.9 0.9 0 1 1 1.27 -1.27 l1.1 1.1 v-4.1 A0.9 0.9 0 0 1 12 10.5 Z" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
    </Svg>
  )
}

export function InviteIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <circle cx="9.2" cy="8" r="3.6" fill="var(--ic)"/>
      <path d="M3 20 a6.2 5.7 0 0 1 12.4 0 a1 1 0 0 1 -1 1.1 H4 a1 1 0 0 1 -1 -1.1 Z" fill="var(--ic)"/>
      <path d="M9.2 4.4 a3.6 3.6 0 0 1 0 7.2 a4.5 4.5 0 0 0 0 -7.2 Z" fill={hi(30)}/>
      <rect x="16.8" y="3" width="1.9" height="8" rx="0.9" fill={hi(20)}/>
      <rect x="13.8" y="6" width="8" height="1.9" rx="0.9" fill={hi(20)}/>
    </Svg>
  )
}

export function HelpIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <circle cx="12" cy="12" r="9" fill="var(--ic)"/>
      <path d="M12 3 a9 9 0 0 1 0 18 a11.2 11.2 0 0 0 0 -18 Z" fill={lo(40)}/>
      <path d="M12 6.6 c1.9 0 3.3 1.25 3.3 3 c0 1.4 -0.85 2.15 -1.65 2.75 c-0.65 0.5 -1 0.85 -1 1.55 a0.9 0.9 0 0 1 -1.8 0 c0 -1.5 0.8 -2.1 1.55 -2.65 c0.65 -0.5 1.1 -0.85 1.1 -1.65 c0 -0.8 -0.65 -1.3 -1.5 -1.3 c-0.75 0 -1.3 0.35 -1.5 1 a0.9 0.9 0 0 1 -1.72 -0.55 C9.15 7.4 10.35 6.6 12 6.6 Z" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
      <circle cx="12" cy="17.3" r="1.1" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
    </Svg>
  )
}

export function DiscordIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <path d="M7.2 5.8 c2.9 -1.2 6.7 -1.2 9.6 0 c1.9 3.1 2.6 6.4 2.3 10 c-1.8 1.5 -3.6 2.3 -5.3 2.8 c-0.4 -0.6 -0.8 -1.2 -1.1 -1.9 c0.7 -0.25 1.35 -0.55 1.95 -0.95 c-0.16 -0.12 -0.32 -0.25 -0.47 -0.38 c-3.1 1.4 -6.35 1.4 -9.4 0 c-0.15 0.13 -0.31 0.26 -0.47 0.38 c0.6 0.4 1.25 0.7 1.95 0.95 c-0.3 0.7 -0.7 1.3 -1.1 1.9 c-1.7 -0.5 -3.5 -1.3 -5.3 -2.8 c-0.25 -3 0.35 -6.3 2.3 -10 Z" fill="var(--ic)"/>
      <path d="M7.2 5.8 c-1.95 3.7 -2.55 7 -2.3 10 c1.5 1.25 3 2.05 4.5 2.55 a20 20 0 0 1 -2.3 -2.85 c0.5 0.25 1 0.45 1.55 0.65 c-1.5 -2.6 -2.05 -5.85 -1.45 -10.35 Z" fill={hi(28)}/>
      <ellipse cx="9" cy="13" rx="1.35" ry="1.5" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
      <ellipse cx="15" cy="13" rx="1.35" ry="1.5" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
    </Svg>
  )
}

export function WritersRoomIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  // Clapboard silhouette — the Writers' Room's 🎬 identity, rebuilt in the
  // Solid Facet style (filled board + hinged slate, no strokes to lose at 20px).
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <rect x="3" y="9.4" width="18" height="10.6" rx="1.6" fill="var(--ic)"/>
      <path d="M3 9.4 h18 v4 H3 Z" fill={lo(30)}/>
      <path d="M3.6 8.9 L20.4 5.2 a1.4 1.4 0 0 0 -1.7 -1.05 L4.65 7.3 a1.4 1.4 0 0 0 -1.05 1.6 Z" fill={hi(28)}/>
      <path d="M6.6 6.85 L9.3 4.6 l2.15 -0.47 L8.75 6.38 Z" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
      <path d="M12.15 5.65 L14.85 3.4 l2.15 -0.47 L14.3 5.18 Z" fill="var(--nav-bg, #07070f)" opacity="0.92"/>
      <rect x="5.4" y="14.6" width="9.4" height="1.7" rx="0.85" fill="var(--nav-bg, #07070f)" opacity="0.85"/>
      <rect x="5.4" y="17.3" width="6" height="1.7" rx="0.85" fill="var(--nav-bg, #07070f)" opacity="0.6"/>
    </Svg>
  )
}

export function FaceOffIcon({ size = 24, color = 'currentColor', _strokeWidth, className, style }) {
  // Two facing cards with a center divider — Face-Off's "two posters, one tap"
  // identity, in the same filled Solid Facet language as the rest of this set.
  return (
    <Svg size={size} color={color} className={className} style={style}>
      <rect x="2.5" y="4" width="8.2" height="16" rx="2" fill="var(--ic)"/>
      <rect x="13.3" y="4" width="8.2" height="16" rx="2" fill={lo(28)}/>
      <path d="M2.5 6 a2 2 0 0 1 2 -2 h4.2 a2 2 0 0 1 2 2 v3.4 H2.5 Z" fill={hi(26)}/>
      <circle cx="12" cy="12" r="2.6" fill="var(--nav-bg, #07070f)"/>
      <circle cx="12" cy="12" r="2.6" fill="none" stroke="var(--ic)" strokeWidth="1.1"/>
    </Svg>
  )
}
